import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll'
import styles from './Modal.module.css'

interface ModalProps {
  open: boolean
  onClose: () => void
  /** Rendered in the header as the accessible name of the dialog. */
  title: string
  /** Optional line under the title, e.g. a period or tag row. */
  eyebrow?: ReactNode
  children: ReactNode
  /** Optional controls pinned to the header, e.g. external links. */
  actions?: ReactNode
}

/**
 * An accessible dialog: focus is trapped and restored, Escape and backdrop
 * clicks close it, background scrolling is frozen, and the whole thing is
 * portalled out of the page flow so no ancestor's overflow can clip it.
 */
export function Modal({ open, onClose, title, eyebrow, children, actions }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useLockBodyScroll(open)
  useFocusTrap(panelRef, open)

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  // A newly opened dialog should start at the top of its content.
  useEffect(() => {
    if (open) bodyRef.current?.scrollTo({ top: 0 })
  }, [open, title])

  const onBackdropPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Listening on pointer*down* (not click) means a text selection that
      // starts inside the panel and drags onto the backdrop won't dismiss the
      // dialog — the press began on the panel, so this never fires.
      if (event.target === event.currentTarget) onClose()
    },
    [onClose],
  )

  if (!open) return null

  return createPortal(
    <div className={styles.backdrop} onPointerDown={onBackdropPointerDown}>
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
          </div>
          <div className={styles.headerActions}>
            {actions}
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close dialog"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>

        <div ref={bodyRef} className={styles.body}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
